import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subject } from 'rxjs';
import * as Highcharts from 'highcharts';
import { SeriesOptionsType, XrangePointOptionsObject } from 'highcharts';

import {
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
} from '@vi-iot/client';
import { ActivatedRoute } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { RemoteDiagnosticsStore } from '@store';
import { SemanticValues, StartStopTempData, TimeType } from '../../diagnostics-constants';
import { DiagnosticsVddStartStopTempService } from '../../diagnostics-vdd-start-stop-temp.service';
import { DiagnosticsHelpers } from '../../diagnostics-helpers';
import { timePhaseChartOptions } from './highcharts-constant';
import {
  AddTimePhaseModalComponent,
  TimePhaseValue,
} from './add-time-phase-modal/add-time-phase-modal.component';

@Component({
  selector: 'app-vdd-start-stop',
  templateUrl: './vdd-start-stop.component.html',
  styleUrls: ['./vdd-start-stop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VddStartStopComponent implements OnInit {
  @Input() startStopTempStructure: RemoteDiagnosticsVddGenericStructure[];
  @Input() matchedValues: RemoteDiagnosticsVddGenericStructure[];
  @Input() rawSingleReadValues: RemoteDiagnosticsSuccessResponseData;
  @Input() entriesStructure: RemoteDiagnosticsVddGenericStructure;
  @Input() isVddEditable: boolean;
  @Input() parameterId: number;
  @Output() setRawSingleReadValuesEventEmitter: EventEmitter<{
    rawSingleReadValues: RemoteDiagnosticsSuccessResponseData;
  }>;

  updateFlag: BehaviorSubject<boolean> = new BehaviorSubject(false);
  chartOptions: Highcharts.Options = timePhaseChartOptions;
  timePhasesToShow: RemoteDiagnosticsVddGenericStructure[] = [];
  ngUnsubscribe: Subject<void> = new Subject<void>();
  highCharts: typeof Highcharts = Highcharts;
  maxNumberOfTimePhases: number;
  isParameterRunning: boolean;
  isModeSelector: boolean;

  readonly timeType = TimeType;
  readonly semanticValues = SemanticValues;

  constructor(
    public diagnosticsVddStartStopTempService: DiagnosticsVddStartStopTempService,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const numberOfTimePhases = this.getTimePhasesNumber(this.entriesStructure.value);
    this.maxNumberOfTimePhases = this.startStopTempStructure.length;
    this.initTimePhases(numberOfTimePhases);

    const gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];
    this.remoteDiagnosticsStore
      .isParameterRunning(gatewaySerial, this.parameterId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((isParameterRunning) => {
        this.isParameterRunning = isParameterRunning;
        this.cdr.markForCheck();
      });
  }

  getModes(index: number): string[] {
    return this.getModeStructure(index).structure.map((element) => element.texttableentry);
  }

  openAddTimePhaseModal(): void {
    const index = this.timePhasesToShow.length;
    const modesOptions = this.isModeSelector ? this.getModes(index) : [];

    this.diagnosticsVddStartStopTempService.isMaxNumberOfTimePhasesReached = false;

    const addTimePhaseDialogRef = this.dialog.open(AddTimePhaseModalComponent, {
      width: '710px',
      panelClass: 'add-time-phase-dialog',
      data: { modesOptions },
      disableClose: true,
      autoFocus: false,
    });

    addTimePhaseDialogRef.afterClosed().subscribe((value: TimePhaseValue) => {
      if (value) {
        this.updateDataAndChart(index, value.startTime, value.stopTime, value.mode);
      }
    });
  }

  onTimePhaseDelete(index: number): void {
    const newData = this.diagnosticsVddStartStopTempService.deleteTimePhase(
      index,
      this.startStopTempStructure,
      this.entriesStructure
    );
    this.updateStartStopTempVDD(newData);
    this.updateChart();
  }

  onModeChange(newValue: string, index: number): void {
    const timePhases = this.diagnosticsVddStartStopTempService.findAndUpdateModeStructure(
      newValue,
      index,
      this.startStopTempStructure
    );
    this.updateStartStopTempVDD({
      structure: timePhases,
      entriesValue: this.entriesStructure.value,
    });
  }

  onStartTimePhaseChange(newValue: string, index: number): void {
    const newData = this.diagnosticsVddStartStopTempService.recalculateTimePhasesByStartTimeChange(
      newValue,
      index,
      this.startStopTempStructure,
      this.entriesStructure
    );
    this.updateStartStopTempVDD(newData);
    this.updateChart();
  }

  onStopTimePhaseChange(newValue: string, index: number): void {
    const newData = this.diagnosticsVddStartStopTempService.recalculateTimePhasesByStopTimeChange(
      newValue,
      index,
      this.startStopTempStructure,
      this.entriesStructure
    );
    this.updateStartStopTempVDD(newData);
    this.updateChart();
  }

  private updateDataAndChart(
    index: number,
    startTime: string,
    stopTime: string,
    mode: string
  ): void {
    const newData = this.diagnosticsVddStartStopTempService.recalculateTimePhasesByAddOne(
      index,
      this.startStopTempStructure,
      this.entriesStructure,
      startTime,
      stopTime,
      mode
    );
    this.updateStartStopTempVDD(newData);
    this.updateChart();
  }

  private updateStartStopTempVDD(data: StartStopTempData): void {
    const dataRecorStructure = DiagnosticsHelpers.getDataRecorStructure(this.rawSingleReadValues);
    const startStopTemp = dataRecorStructure.structure.find(
      (structure) => structure.semantic === SemanticValues.STARTSTOPTEMP
    );
    const entries = dataRecorStructure.structure.find(
      (structure) => structure.semantic === SemanticValues.ENTRIES
    );
    startStopTemp.structure = data.structure;
    entries.value = data.entriesValue;
  }

  private getModeStructure(index: number): RemoteDiagnosticsVddGenericStructure {
    const startStopTemp = this.matchedValues.find(
      (value) => value.semantic === SemanticValues.STARTSTOPTEMP
    );

    return startStopTemp.structure[index].structure.find(
      (element) => element.semantic === SemanticValues.ENUMCHOICE
    );
  }

  private initTimePhases(numberOfTimePhases: number): void {
    const dataForSeries: XrangePointOptionsObject[] = [];
    for (let i = 0; i < numberOfTimePhases; i++) {
      const structure = this.startStopTempStructure[i];

      this.timePhasesToShow.push(structure);
      this.setModeParameter(i);

      dataForSeries.push(this.updateChartOptionsSeries(structure, i));
    }
    this.setChartOptionsSeries(dataForSeries);
  }

  private updateChart(): void {
    const dataForSeries = this.timePhasesToShow.map((structure, idx) => {
      this.setModeParameter(idx);

      return this.updateChartOptionsSeries(structure, idx);
    });

    this.setChartOptionsSeries(dataForSeries);
    this.updateFlag.next(true);
    this.setRawSingleReadValuesEventEmitter.emit({
      rawSingleReadValues: this.rawSingleReadValues,
    });
  }

  private updateChartOptionsSeries(
    structure: RemoteDiagnosticsVddGenericStructure,
    index: number
  ): XrangePointOptionsObject {
    const hourMinuteStructure = this.diagnosticsVddStartStopTempService.getHourMinuteStructure(
      structure
    );
    const startValue = this.getValueForDateSeries(
      Number(hourMinuteStructure.startHourStructure.value),
      Number(hourMinuteStructure.startMinuteStructure.value)
    );
    const stopValue = this.getValueForDateSeries(
      Number(hourMinuteStructure.stopHourStructure.value),
      Number(hourMinuteStructure.stopMinuteStructure.value)
    );

    return {
      x: startValue,
      x2: stopValue,
      y: index,
      color: '#DEDEDE',
      pointWidth: 40 + index * 10,
    } as XrangePointOptionsObject;
  }

  private getValueForDateSeries(hours: number, minutes: number): number {
    return Date.UTC(1970, 1, 1, hours, minutes, 0);
  }

  private setModeParameter(index: number): void {
    const modeParameter = this.getModeStructure(index);
    if (modeParameter) {
      this.isModeSelector = true;
    }
  }

  private setChartOptionsSeries(data: XrangePointOptionsObject[]): void {
    this.chartOptions.series = [
      {
        ...this.chartOptions.series[0],
        data,
      } as SeriesOptionsType,
    ];
  }

  private getTimePhasesNumber(entryValue: string): number {
    const result = Number(entryValue);
    if (isNaN(result)) {
      return 0;
    }

    return result;
  }
}
